import { FC, useState } from "react";
import { FormProvider, useForm } from "react-hook-form";
import { z } from "zod";
import { zodResolver } from "@hookform/resolvers/zod";
import { useRouter } from "next/router";
import { validatePhoneNumberLength } from "libphonenumber-js";
import { useTranslation } from "next-i18next";
import { Category } from "@prisma/client";

import TextInput from "@components/common/inputs/text-input";
import SubmitButton from "@components/common/inputs/submit-button";
import TextAreaInput from "@components/common/inputs/text-area-input";
import StatusDialog from "@components/common/status-dialog";
import { commonFormOptions } from "@config/forms";
import PhoneNumberInput from "@components/common/inputs/phone-input";
import Loader from "@components/common/loader";

interface Props {
  category: Category;
  url?: string;
}

const noEmpty = "no-empty";

const formSchema = z.object({
  category: z.string().min(1, noEmpty),
  firstName: z.string().min(1, noEmpty),
  lastName: z.string().min(1, noEmpty),
  email: z.string().email("email"),
  phone: z.string().refine((phone) => !validatePhoneNumberLength(phone)),
  message: z.string().min(1, noEmpty),
  url: z.string().url("url"),
});

export type InquiryFormSchema = z.infer<typeof formSchema>;

const InquiryComponent: FC<Props> = ({ category, url }) => {
  const router = useRouter();
  const categoryName = router.locale === "en" ? category.nameEn : category.name;
  const form = useForm<InquiryFormSchema>({
    ...commonFormOptions,
    defaultValues: { category: categoryName, url },
    resolver: zodResolver(formSchema),
  });
  const [success, setSuccess] = useState(false);
  const [message, setMessage] = useState("");
  const [open, setOpen] = useState(false);
  const [loading, setLoading] = useState(false);
  const { t } = useTranslation("forms", { keyPrefix: "inquiry" });

  const { register, handleSubmit, watch, setValue } = form;
  const phoneNumber = watch("phone");
  const { ref: _, ...registerPhone } = register("phone");

  const handleFormSend = async (data: InquiryFormSchema) => {
    setLoading(true);
    const response = await fetch("/api/inquiry", {
      method: "POST",
      headers: { "Content-Type": "application/json" },
      body: JSON.stringify(data),
    });

    if (!response.ok) {
      setMessage(t("error"));
      setSuccess(false);
      setOpen(true);
      setLoading(false);
      return;
    }

    setMessage(t("success"));
    setSuccess(true);
    setOpen(true);
    setLoading(false);
  };

  return (
    <FormProvider {...form}>
      <Loader {...{ loading }} />
      <form
        onSubmit={handleSubmit(handleFormSend)}
        className="grid grid-cols-12 gap-2"
      >
        <div className="col-span-12 md:col-span-4">
          <TextInput
            {...{
              ...register("category"),
              label: t("category"),
              placeholder: "",
              required: true,
              showLabel: true,
              disabled: true,
            }}
          />
        </div>
        <div className="col-span-12 md:col-span-8">
          <TextInput
            {...{
              ...register("url"),
              label: "URL",
              placeholder: t("url"),
              required: true,
              showLabel: true,
              disabled: !!url,
            }}
          />
        </div>
        <div className="col-span-12 md:col-span-6">
          <TextInput
            {...{
              ...register("firstName"),
              label: t("name"),
              placeholder: t("name"),
              required: true,
              showLabel: true,
            }}
          />
        </div>
        <div className="col-span-12 md:col-span-6">
          <TextInput
            {...{
              ...register("lastName"),
              label: t("last-name"),
              placeholder: t("last-name"),
              required: true,
              showLabel: true,
            }}
          />
        </div>
        <div className="col-span-12 md:col-span-6">
          <TextInput
            {...{
              ...register("email"),
              label: "E-mail",
              placeholder: "E-mail",
              required: true,
              showLabel: true,
            }}
          />
        </div>
        <div className="col-span-12 md:col-span-6">
          <PhoneNumberInput
            {...{
              ...registerPhone,
              label: t("phone"),
              placeholder: t("phone"),
              showLabel: true,
              required: true,
              value: phoneNumber,
              setValue: (phoneNumber) =>
                setValue("phone", phoneNumber, { shouldValidate: true }),
            }}
          />
        </div>

        <div className="col-span-12">
          <TextAreaInput
            {...{
              ...register("message"),
              label: t("inquiry"),
              placeholder: t("details"),
              required: true,
              showLabel: true,
            }}
          />
        </div>

        <div className="col-span-12">
          <SubmitButton {...{ text: t("send-button") }} />
        </div>
        <StatusDialog
          {...{
            message,
            open,
            success,
            onFailureClose: () => setOpen(false),
            onSuccessClose: () => router.push("/"),
          }}
        />
      </form>
    </FormProvider>
  );
};

export default InquiryComponent;
